import { isEmptyObject as isValidObject } from 'common/utilities/utils';
import { CONTACTKFC_UTILS } from '../Constants';
import {
  NUMBER_ONLY_REGEX,
  FULL_NAME_STRING_REGEX,
  NAME_HALF_STRING_REGEX,
  TENANT_DELIVERY_ADDRESS_MAPPING,
  FROM_CONTACT,
  DATE_FORMATS,
} from 'common/constants/SharedConstants';
import { parseAddress } from '@kfc-global/react-shared/utils/AddressParser';
import { convertTwentyFourHrToTwelveHr } from 'common/utilities/dayJsUtils';

export const validateName = name => {
  const regex = NAME_HALF_STRING_REGEX;
  return regex.test(String(name).toLowerCase());
};

export const validatePostCode = postcode => {
  const regex = NUMBER_ONLY_REGEX;
  return regex.test(String(postcode).toLowerCase());
};

export const validateFullName = name1 => {
  const regex = FULL_NAME_STRING_REGEX;
  return regex.test(String(name1).toLowerCase());
};

export const validateEmail = email => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
};

export const validatePhone = phone => {
  const regex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return regex.test(String(phone));
};

export const validDate = date => {
  if (date !== undefined) {
    const monthFormat = date.getMonth() + 1 > 9 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1);
    const dateFormat = date.getDate() > 9 ? date.getDate() : '0' + date.getDate();
    return date.getFullYear() + '/' + monthFormat + '/' + dateFormat;
  }
};

export const validTime = time => {
  return time ? convertTwentyFourHrToTwelveHr(time.getHours(), time.getMinutes(), DATE_FORMATS.hh_mm_A) : false;
};

export const isValidForm = (formData, reCaptchaToken) => {
  const { wantToKnowText, selectedLetUsKnow, firstNameText, lastNameText, phoneNoText, emailText } = formData;
  if (wantToKnowText?.length === 0 || wantToKnowText?.length > 200) {
    return false;
  }
  if (!validateName(firstNameText)) {
    return false;
  }
  if (!validateName(lastNameText)) {
    return false;
  }
  if (!validateFullName(`${firstNameText}${lastNameText}`)) {
    return false;
  }
  if (!validatePhone(phoneNoText)) {
    return false;
  }
  if (!validateEmail(emailText)) {
    return false;
  }
  if (!isValidObject(selectedLetUsKnow)) {
    return false;
  }
  if (!reCaptchaToken) {
    return false;
  }
  return true;
};

export const isValidAccountForm = formData => {
  const { firstNameText, lastNameText, phoneNoText, emailText } = formData;
  if (!validateFullName(firstNameText)) {
    return false;
  }
  if (!validateFullName(lastNameText)) {
    return false;
  }

  if (!validatePhone(phoneNoText)) {
    return false;
  }
  if (!validateEmail(emailText)) {
    return false;
  }
  return true;
};

export const getFeebackRequest = (formData, tenantId, reCaptchaToken) => {
  const payload = {
    data: [
      {
        key: 'message',
        value: formData.wantToKnowText,
      },
      {
        key: 'category',
        value: formData.selectedLetUsKnow.key,
      },
      {
        key: 'firstName',
        value: formData.firstNameText,
      },
      {
        key: 'lastName',
        value: formData.lastNameText,
      },
      {
        key: 'email',
        value: formData.emailText,
      },
      {
        key: 'phone',
        value: formData.phoneNoText,
      },
      {
        key: 'language',
        value: 'en-US',
      },
      {
        key: 'captchaResponse',
        value: reCaptchaToken,
      },
    ],
    attachments: [],
  };
  return { tenantId, payload };
};

export const getContactKFCRequest = (contactKFCData, tenantId) => {
  const payload = {
    data: [
      {
        key: CONTACTKFC_UTILS.FIRSTNAME,
        value: contactKFCData.fullname,
      },
      {
        key: CONTACTKFC_UTILS.CALLNATURE,
        value: contactKFCData.helpDDOptions,
      },
      {
        key: CONTACTKFC_UTILS.CATEGORY,
        value: contactKFCData.enquiryDDOptions,
      },
      {
        key: CONTACTKFC_UTILS.SUBCODE,
        value: contactKFCData.specificDDOptions,
      },
      {
        key: CONTACTKFC_UTILS.DESCRIPTION,
        value: contactKFCData.detailsArea,
      },
      {
        key: CONTACTKFC_UTILS.LASTNAME,
        value: contactKFCData.publicationdetails,
      },
      {
        key: CONTACTKFC_UTILS.PHONE,
        value: contactKFCData.phonenumber,
      },
      {
        key: CONTACTKFC_UTILS.EMAIL,
        value: contactKFCData.emailaddress,
      },
      {
        key: CONTACTKFC_UTILS.STOREID,
        value: contactKFCData?.sugPinCode ? contactKFCData?.sugPinCode : '',
      },
      {
        key: CONTACTKFC_UTILS.TYPEOFVISIT,
        value: contactKFCData?.sortvisit ? contactKFCData?.sortvisit : '',
      },
      {
        key: CONTACTKFC_UTILS.TYPEOFMEAL,
        value: contactKFCData?.sortmeal ? contactKFCData?.sortmeal : '',
      },
      {
        key: CONTACTKFC_UTILS.ORDER_NUMBER,
        value: contactKFCData?.ordernumber ? contactKFCData?.ordernumber : '',
      },
      {
        key: CONTACTKFC_UTILS.VISITDATE,
        value: contactKFCData?.visitedDate ? validDate(contactKFCData.visitedDate) : '',
      },
      {
        key: CONTACTKFC_UTILS.VISITTIME,
        value: validTime(contactKFCData.visitedTime) ?? '',
      },
      {
        key: CONTACTKFC_UTILS.LANGUAGE,
        value: 'en-US',
      },
    ],
    attachments: [],
  };
  return { tenantId, payload };
};

export const setAddressDetails = (
  flag,
  addressDetails,
  setValue,
  setSaveAddress,
  clearSuggestions,
  setDataCheck,
  setPostCodeError,
  setError,
  contactKFCData,
) => {
  const deliveryAddressMapping = JSON.parse(localStorage.getItem(TENANT_DELIVERY_ADDRESS_MAPPING));
  const {
    addressLines = [],
    country = '',
    city = '',
    pinCode = '',
    state = '',
  } = parseAddress(addressDetails.address_components, deliveryAddressMapping);
  if (flag === FROM_CONTACT) {
    if (validatePostCode(pinCode)) {
      setPostCodeError(false);
      setError(false);
      setValue(pinCode);
    } else {
      setPostCodeError(true);
    }
    setDataCheck({ ...contactKFCData, sugPinCode: pinCode });
  } else {
    setValue(addressLines?.[1]);
    setDataCheck();
  }
  setSaveAddress(prevState => ({
    ...prevState,
    country,
    addressLines,
    city,
    state,
    pinCode,
  }));
  setTimeout(() => {
    clearSuggestions();
  }, 800);
};
